import React, { useRef } from 'react';
import {
    Box,
    IconButton,
    Badge,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverHeader,
    PopoverBody,
    PopoverFooter,
    PopoverArrow,
    PopoverCloseButton,
    Portal,
    Button,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/react';
import { BellIcon } from '@chakra-ui/icons';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import NotificationList from './NotificationList';

const NotificationBell = () => {
    const {
        notifications,
        unreadCount,
        isLoading,
        markAsRead,
        markAllAsRead
    } = useNotifications();

    const { isOpen, onOpen, onClose } = useDisclosure(); // Popover
    const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure(); // Modal

    const navigate = useNavigate();

    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }
        if (notification.link) {
            navigate(notification.link);
            onClose(); // Close Popover
            onModalClose(); // Close Modal if open
        }
    };

    const handleViewAll = () => {
        onClose(); // Close popover
        onModalOpen(); // Open modal
    };

    return (
        <Box position="relative">
            <Popover
                onOpen={() => {
                    onOpen();
                    if (unreadCount > 0) {
                        markAllAsRead();
                    }
                }}
                onClose={onClose}
                isOpen={isOpen}
                placement="bottom-end"
                closeOnBlur={true}
                closeOnEsc={true}
            >
                <PopoverTrigger>
                    <Box display="inline-block">
                        <IconButton
                            icon={<BellIcon fontSize="24px" />}
                            variant="ghost"
                            colorScheme="gray"
                            aria-label="Notifications"
                            color="gray.600"
                        />
                        {unreadCount > 0 && (
                            <Badge
                                position="absolute"
                                top="-2px"
                                right="-2px"
                                colorScheme="red"
                                borderRadius="full"
                                variant="solid"
                                minW="18px"
                                textAlign="center"
                                fontSize="xs"
                            >
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </Badge>
                        )}
                    </Box>
                </PopoverTrigger>
                {/* Portal 是必要的，不是為了美觀：原本 PopoverContent 直接渲染在 header 內，
                    而 header 在 DOM 上排在內容區之前、又沒有自己的 zIndex，所以內容區裡任何
                    帶正 zIndex 的東西都會蓋在它上面。實際災情：請假/訂餐月曆的日期格是
                    position="relative" + zIndex={1}（LeavesPage.jsx:278/388），打開通知中心時
                    那些圓圈會整片浮在通知內容上（user 實機截圖）。
                    改用 Portal 渲染到 document root，就不再與頁面內容競爭堆疊順序，
                    也一併解決訂餐頁同一個月曆元件造成的相同問題。
                    另外明確指定 bg/zIndex，不依賴主題預設，避免日後有人改 Popover 樣式時又破。 */}
                <Portal>
                <PopoverContent width="400px" maxH="500px" overflowY="hidden" color="gray.800"
                    bg="white" zIndex="popover">
                    <PopoverArrow />
                    <PopoverHeader fontWeight="bold" display="flex" justifyContent="space-between" alignItems="center">
                        通知中心
                        {unreadCount > 0 && (
                            <Button size="xs" variant="ghost" colorScheme="blue" onClick={markAllAsRead}>
                                全部已讀
                            </Button>
                        )}
                    </PopoverHeader>
                    <PopoverCloseButton />
                    <PopoverBody p={0} overflowY="auto" maxH="400px">
                        <NotificationList
                            notifications={notifications}
                            isLoading={isLoading}
                            onItemClick={handleNotificationClick}
                            limit={5}
                        />
                    </PopoverBody>
                    <PopoverFooter display="flex" justifyContent="center">
                        <Button size="xs" variant="link" colorScheme="gray" onClick={handleViewAll}>
                            查看全部歷史紀錄
                        </Button>
                    </PopoverFooter>
                </PopoverContent>
                </Portal>
            </Popover>

            {/* History Modal */}
            <Modal isOpen={isModalOpen} onClose={onModalClose} size="lg" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent maxH="80vh">
                    <ModalHeader>通知歷史紀錄</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody p={0}>
                        <NotificationList
                            notifications={notifications}
                            isLoading={isLoading}
                            onItemClick={handleNotificationClick}
                            // No limit here
                            emptyMessage="沒有歷史紀錄"
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default NotificationBell;
